import AddIcon from '@mui/icons-material/Add';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import {Button, Divider, IconButton, Popover, Stack, TextField, Tooltip} from '@mui/material';
import {useEditorState, type Editor} from '@tiptap/react';
import {useState, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {canFormatHtmlSource, formatHtmlSource, isEmailLink, type SourceSelection} from './htmlSourceFormatting';

interface Props {
    editor: Editor | null;
    disabled: boolean;
    preview: boolean;
    onInsertVariable: (anchor: HTMLElement) => void;
}

export default function AutomationHtmlToolbar({editor, disabled, preview, onInsertVariable}: Props) {
    const {t} = useTranslation();
    const [link, setLink] = useState<{anchor: HTMLElement; range: SourceSelection} | null>(null);
    const [href, setHref] = useState('');
    const state = useEditorState({editor, selector: ({editor: instance}) => ({
        undo: instance?.can().undo() ?? false,
        redo: instance?.can().redo() ?? false,
        format: instance ? canFormatHtmlSource(instance) : false,
    })});
    const cannotEdit = disabled || preview || !editor;
    const cannotFormat = cannotEdit || !state?.format;
    const closeLink = () => {setLink(null); editor?.commands.focus();};
    const applyLink = () => {
        if (!editor || !link || cannotFormat) return;
        formatHtmlSource(editor, 'link', link.range, href.trim());
        closeLink();
    };
    const button = (label: string, icon: ReactNode, onClick: (anchor: HTMLElement) => void, inactive: boolean) => (
        <Tooltip title={t(label)}>
            <span><IconButton size="small" aria-label={t(label)} disabled={inactive}
                onMouseDown={event => event.preventDefault()} onClick={event => onClick(event.currentTarget)}>
                {icon}
            </IconButton></span>
        </Tooltip>
    );

    return <>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.25} role="group" aria-label={t('Email formatting')}
            sx={{px: 0.75, py: 0.5, borderBottom: '1px solid', borderColor: 'divider'}}>
            {button('Bold', <FormatBoldIcon fontSize="small"/>, () => editor && formatHtmlSource(editor, 'bold'), cannotFormat)}
            {button('Italic', <FormatItalicIcon fontSize="small"/>, () => editor && formatHtmlSource(editor, 'italic'), cannotFormat)}
            {button('Underline', <FormatUnderlinedIcon fontSize="small"/>, () => editor && formatHtmlSource(editor, 'underline'), cannotFormat)}
            {button('Bullet list', <FormatListBulletedIcon fontSize="small"/>, () => editor && formatHtmlSource(editor, 'bulletList'), cannotFormat)}
            {button('Numbered list', <FormatListNumberedIcon fontSize="small"/>, () => editor && formatHtmlSource(editor, 'orderedList'), cannotFormat)}
            {button('Insert link', <InsertLinkIcon fontSize="small"/>, anchor => {
                if (!editor) return;
                setHref('');
                setLink({anchor, range: {from: editor.state.selection.from, to: editor.state.selection.to}});
            }, cannotFormat)}
            <Divider orientation="vertical" flexItem sx={{mx: 0.5}}/>
            <Button size="small" color="inherit" startIcon={<AddIcon/>} disabled={cannotEdit}
                onMouseDown={event => event.preventDefault()} onClick={event => onInsertVariable(event.currentTarget)}>
                {t('Insert variable')}
            </Button>
            <Stack direction="row" sx={{ml: 'auto'}}>
                {button('Undo', <UndoIcon fontSize="small"/>, () => editor?.commands.undo(), disabled || !state?.undo)}
                {button('Redo', <RedoIcon fontSize="small"/>, () => editor?.commands.redo(), disabled || !state?.redo)}
            </Stack>
        </Stack>
        <Popover open={!!link && !disabled && !preview} anchorEl={link?.anchor} onClose={closeLink} disableRestoreFocus
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}>
            <Stack component="form" gap={1.5} sx={{p: 2, width: 320, maxWidth: '80vw'}}
                onSubmit={event => {event.preventDefault(); applyLink();}}>
                <TextField autoFocus size="small" label={t('URL')} value={href} placeholder="https://"
                    onChange={event => setHref(event.target.value)}
                    error={!!href.trim() && !isEmailLink(href.trim())}
                    helperText={href.trim() && !isEmailLink(href.trim()) ? t('Enter a valid link or a mapped variable.') : undefined}/>
                <Stack direction="row" justifyContent="flex-end" gap={1}>
                    <Button onClick={closeLink}>{t('Cancel')}</Button>
                    <Button type="submit" variant="contained" disabled={!isEmailLink(href.trim())}>{t('Apply')}</Button>
                </Stack>
            </Stack>
        </Popover>
    </>;
}
